import type { Lang } from './languages.js';
import { keySignature, subSignatures, TILE_COUNT, tileCounts, toTileKey } from './tiles.js';

/**
 * Frequency tiers (from wordfreq Zipf values, see DICTIONARY_SOURCES.md):
 * 1 = very common, 2 = common, 3 = valid but rare (or unknown to the frequency list).
 */
export type Tier = 1 | 2 | 3;

export interface LexiconEntry {
  key: string;
  /** Correct spellings sharing this key, most frequent first. */
  displays: string[];
  tier: Tier;
}

export interface LexiconBuildInput {
  lang: Lang;
  version: string;
  /** Display forms (correct spelling). */
  words: Iterable<string>;
  /** Display form → tier (1 or 2). Missing forms are tier 3. */
  tiers?: ReadonlyMap<string, Tier>;
  /** Display forms excluded by the content policy (used only to explain rejections). */
  excluded?: Iterable<string>;
  minLength?: number;
  maxLength?: number;
}

export interface CompactLexicon {
  lang: Lang;
  version: string;
  minLength: number;
  maxLength: number;
  keysJoined: string;
  keyOff: Uint32Array;
  tiers: Uint8Array;
  displaysJoined: string;
  displayOff: Uint32Array;
  sigsJoined: string;
  sigOff: Uint32Array;
  sigOrder: Uint32Array;
  excludedKeys: string[];
}

/**
 * Memory-compact, immutable lexicon for one language.
 * Keys are stored in a single string with offsets and looked up by binary search.
 * An anagram-signature index allows enumerating every playable word of a rack quickly.
 */
export class Lexicon {
  readonly lang: Lang;
  readonly version: string;
  readonly size: number;
  readonly minLength: number;
  readonly maxLength: number;
  private readonly keysJoined: string;
  private readonly keyOff: Uint32Array;
  private readonly tiers: Uint8Array;
  private readonly displaysJoined: string;
  private readonly displayOff: Uint32Array;
  private readonly sigsJoined: string;
  private readonly sigOff: Uint32Array;
  private readonly sigOrder: Uint32Array;
  private readonly excludedKeys: ReadonlySet<string>;
  private letterFreqCache: Float64Array | null = null;

  private constructor(init: {
    lang: Lang;
    version: string;
    minLength: number;
    maxLength: number;
    keysJoined: string;
    keyOff: Uint32Array;
    tiers: Uint8Array;
    displaysJoined: string;
    displayOff: Uint32Array;
    sigsJoined: string;
    sigOff: Uint32Array;
    sigOrder: Uint32Array;
    excludedKeys: ReadonlySet<string>;
  }) {
    this.lang = init.lang;
    this.version = init.version;
    this.minLength = init.minLength;
    this.maxLength = init.maxLength;
    this.keysJoined = init.keysJoined;
    this.keyOff = init.keyOff;
    this.tiers = init.tiers;
    this.displaysJoined = init.displaysJoined;
    this.displayOff = init.displayOff;
    this.sigsJoined = init.sigsJoined;
    this.sigOff = init.sigOff;
    this.sigOrder = init.sigOrder;
    this.excludedKeys = init.excludedKeys;
    this.size = init.keyOff.length - 1;
  }

  /** Serialisable form (strings + typed arrays), e.g. to build in a Web Worker and transfer. */
  toCompact(): CompactLexicon {
    return {
      lang: this.lang,
      version: this.version,
      minLength: this.minLength,
      maxLength: this.maxLength,
      keysJoined: this.keysJoined,
      keyOff: this.keyOff,
      tiers: this.tiers,
      displaysJoined: this.displaysJoined,
      displayOff: this.displayOff,
      sigsJoined: this.sigsJoined,
      sigOff: this.sigOff,
      sigOrder: this.sigOrder,
      excludedKeys: [...this.excludedKeys],
    };
  }

  static fromCompact(c: CompactLexicon): Lexicon {
    return new Lexicon({ ...c, excludedKeys: new Set(c.excludedKeys) });
  }

  static build(input: LexiconBuildInput): Lexicon {
    const minLength = input.minLength ?? 3;
    const maxLength = input.maxLength ?? 9;
    const groups = new Map<string, { displays: string[]; tier: Tier }>();
    for (const raw of input.words) {
      const display = raw.normalize('NFC');
      const key = toTileKey(display, input.lang);
      if (key === null || key.length < minLength || key.length > maxLength) continue;
      const tier: Tier = input.tiers?.get(display) ?? 3;
      const g = groups.get(key);
      if (g) {
        if (!g.displays.includes(display)) g.displays.push(display);
        if (tier < g.tier) g.tier = tier;
      } else {
        groups.set(key, { displays: [display], tier });
      }
    }
    const keys = [...groups.keys()].sort(compareStrings);
    const n = keys.length;
    const keyOff = new Uint32Array(n + 1);
    const tiers = new Uint8Array(n);
    const displayOff = new Uint32Array(n + 1);
    const keyParts: string[] = [];
    const displayParts: string[] = [];
    let kPos = 0;
    let dPos = 0;
    for (let i = 0; i < n; i++) {
      const key = keys[i]!;
      const g = groups.get(key)!;
      const tierOf = (d: string): number => input.tiers?.get(d) ?? 3;
      g.displays.sort((a, b) => tierOf(a) - tierOf(b) || compareStrings(a, b));
      const joined = g.displays.join('|');
      keyOff[i] = kPos;
      displayOff[i] = dPos;
      keyParts.push(key);
      displayParts.push(joined);
      kPos += key.length;
      dPos += joined.length;
      tiers[i] = g.tier;
    }
    keyOff[n] = kPos;
    displayOff[n] = dPos;

    const sigs = keys.map((k) => keySignature(k));
    const order = Array.from({ length: n }, (_, i) => i).sort(
      (a, b) => compareStrings(sigs[a]!, sigs[b]!) || a - b,
    );
    const sigOrder = new Uint32Array(order);
    const sigOff = new Uint32Array(n + 1);
    const sigParts: string[] = [];
    let sPos = 0;
    for (let j = 0; j < n; j++) {
      const s = sigs[order[j]!]!;
      sigOff[j] = sPos;
      sigParts.push(s);
      sPos += s.length;
    }
    sigOff[n] = sPos;

    const excludedKeys = new Set<string>();
    for (const e of input.excluded ?? []) {
      const k = toTileKey(e, input.lang);
      if (k !== null && !groups.has(k)) excludedKeys.add(k);
    }

    return new Lexicon({
      lang: input.lang,
      version: input.version,
      minLength,
      maxLength,
      keysJoined: keyParts.join(''),
      keyOff,
      tiers,
      displaysJoined: displayParts.join(''),
      displayOff,
      sigsJoined: sigParts.join(''),
      sigOff,
      sigOrder,
      excludedKeys,
    });
  }

  keyAt(i: number): string {
    return this.keysJoined.slice(this.keyOff[i]!, this.keyOff[i + 1]!);
  }

  tierAt(i: number): Tier {
    return this.tiers[i] as Tier;
  }

  displaysAt(i: number): string[] {
    return this.displaysJoined.slice(this.displayOff[i]!, this.displayOff[i + 1]!).split('|');
  }

  entryAt(i: number): LexiconEntry {
    return { key: this.keyAt(i), displays: this.displaysAt(i), tier: this.tierAt(i) };
  }

  /** Index of a key or -1. */
  indexOf(key: string): number {
    let lo = 0;
    let hi = this.size - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      const c = compareSlice(this.keysJoined, this.keyOff[mid]!, this.keyOff[mid + 1]!, key);
      if (c === 0) return mid;
      if (c < 0) lo = mid + 1;
      else hi = mid - 1;
    }
    return -1;
  }

  has(key: string): boolean {
    return this.indexOf(key) >= 0;
  }

  lookup(key: string): LexiconEntry | null {
    const i = this.indexOf(key);
    return i < 0 ? null : this.entryAt(i);
  }

  /** True when the key is a real word excluded by the content policy. */
  isExcluded(key: string): boolean {
    return this.excludedKeys.has(key);
  }

  /** Indices of every key formable with the rack. */
  formableIndices(rack: readonly string[]): number[] {
    const out: number[] = [];
    const maxLen = Math.min(this.maxLength, rack.length);
    for (const sig of subSignatures(rack, this.minLength, maxLen)) {
      let j = this.lowerBoundSig(sig);
      while (
        j < this.size &&
        compareSlice(this.sigsJoined, this.sigOff[j]!, this.sigOff[j + 1]!, sig) === 0
      ) {
        out.push(this.sigOrder[j]!);
        j++;
      }
    }
    return out;
  }

  formableEntries(rack: readonly string[]): LexiconEntry[] {
    return this.formableIndices(rack).map((i) => this.entryAt(i));
  }

  private lowerBoundSig(sig: string): number {
    let lo = 0;
    let hi = this.size;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (compareSlice(this.sigsJoined, this.sigOff[mid]!, this.sigOff[mid + 1]!, sig) < 0)
        lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /**
   * Relative frequency of each tile among common (tier ≤ maxTier) words, used to weight draws.
   * Computed lazily and cached.
   */
  letterFrequencies(maxTier: Tier = 2): Float64Array {
    if (this.letterFreqCache && maxTier === 2) return this.letterFreqCache;
    const freq = new Float64Array(TILE_COUNT);
    let total = 0;
    for (let i = 0; i < this.size; i++) {
      if (this.tiers[i]! > maxTier) continue;
      const counts = tileCounts(this.keyAt(i));
      for (let t = 0; t < TILE_COUNT; t++) {
        const c = counts[t]!;
        if (c > 0) {
          freq[t] = freq[t]! + c;
          total += c;
        }
      }
    }
    if (total > 0) for (let t = 0; t < TILE_COUNT; t++) freq[t] = freq[t]! / total;
    if (maxTier === 2) this.letterFreqCache = freq;
    return freq;
  }

  /** Counts per tier, for manifests and admin dashboards. */
  tierCounts(): Record<Tier, number> {
    const out: Record<Tier, number> = { 1: 0, 2: 0, 3: 0 };
    for (let i = 0; i < this.size; i++) out[this.tiers[i] as Tier]++;
    return out;
  }
}

export function compareStrings(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareSlice(joined: string, start: number, end: number, target: string): number {
  const len = end - start;
  const n = Math.min(len, target.length);
  for (let j = 0; j < n; j++) {
    const d = joined.charCodeAt(start + j) - target.charCodeAt(j);
    if (d !== 0) return d;
  }
  return len - target.length;
}

/** Parses a words file: one display form per line; blank lines and lines starting with # ignored. */
export function parseWordList(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split('\n')) {
    const w = line.trim();
    if (w.length === 0 || w.startsWith('#')) continue;
    out.push(w);
  }
  return out;
}

/** Parses a tiers file: `tier<TAB>display` per line. */
export function parseTierList(text: string): Map<string, Tier> {
  const out = new Map<string, Tier>();
  for (const line of text.split('\n')) {
    if (line.length === 0 || line.startsWith('#')) continue;
    const tab = line.indexOf('\t');
    if (tab < 0) continue;
    const tier = Number(line.slice(0, tab));
    if (tier === 1 || tier === 2)
      out.set(
        line
          .slice(tab + 1)
          .trim()
          .normalize('NFC'),
        tier,
      );
  }
  return out;
}

/** Registry of the lexicons available to a game, by language. */
export type LexiconSet = Partial<Record<Lang, Lexicon>>;

export function requireLexicon(set: LexiconSet, lang: Lang): Lexicon {
  const lex = set[lang];
  if (!lex) throw new Error(`Lexicon not loaded: ${lang}`);
  return lex;
}
